import { MutableRefObject } from "react";
import { LightPointErrorType } from "../../models/LionError";
import { useApiClient } from "../apiClient/apiClient";
import { getParamsURLEncoded } from "../apiParamFactory";
import { FetchLightPointsQuery, GetLightPointsResponse } from "./lightPointsApi.types";

export const useLightPointsApi = (customerScopeRef: MutableRefObject<string|undefined>) => {
  const apiClient = useApiClient();

  /**
   * Fetches lighting points for the current customerScope.
   *
   * Performance optimization (Ticket 5576273):
   * When bounding coordinates (swLatitude, swLongitude, neLatitude, neLongitude) are present in the query,
   * requests /v2/lightingpoints/by-bounds to fetch only the points inside the user's visible map viewport
   * (up to 1,000 items). Otherwise calls /v2/lightingpoints/search for initial center lookup or asset number search.
   *
   * @param query - Optional query parameters (viewport bounding box or commune center/number)
   * @returns Promise resolving to lighting points response with commune center and point data
   */
  async function getLightPoints(
    query?: FetchLightPointsQuery,
  ): Promise<GetLightPointsResponse> {
    let effectiveQuery = query;
    if (query && !("swLatitude" in query) && "ceLatitude" in query && query.ceLatitude !== undefined && query.ceLongitude !== undefined) {
      const deltaLat = 0.008;
      const deltaLon = 0.012;
      effectiveQuery = {
        ...query,
        swLatitude: query.ceLatitude - deltaLat,
        swLongitude: query.ceLongitude - deltaLon,
        neLatitude: query.ceLatitude + deltaLat,
        neLongitude: query.ceLongitude + deltaLon,
      };
    }

    if (effectiveQuery && "swLatitude" in effectiveQuery && effectiveQuery.swLatitude !== undefined) {
      const searchParams = getParamsURLEncoded(effectiveQuery, customerScopeRef.current);
      return apiClient.get<GetLightPointsResponse>(
        `v2/lightingpoints/by-bounds?${searchParams.toString()}`, LightPointErrorType.FETCH_LIGHTPOINTS
      );
    }
    const params: { lightingNo?: string } = {};
    if (query && "lightingNo" in query && (query as any).lightingNo) {
      params.lightingNo = (query as any).lightingNo;
    }
    const searchParams = getParamsURLEncoded(params, customerScopeRef.current);
    return apiClient.get<GetLightPointsResponse>(
      `v2/lightingpoints/search?${searchParams.toString()}`, LightPointErrorType.FETCH_LIGHTPOINTS
    );
  }

  /**
   * Searches lighting points by asset number within the current customerScope.
   *
   * @param lightingNo - Optional lighting point number
   * @returns Promise resolving to lighting points response
   */
  const searchLightPoints = (lightingNo?: string) => apiClient.get<GetLightPointsResponse>(
    "v2/lightingpoints/search?" + getParamsURLEncoded({ lightingNo }, customerScopeRef.current).toString(),
    LightPointErrorType.FETCH_LIGHTPOINTS
  );

  return {
    getLightPoints,
    searchLightPoints,
  };
};
